<template>
  <div ref="div">
    <a
      v-for="(item, idx) in items"
      :key="idx"
      :href="getMediaUrl(item)"
      :class="[item.type !== 'reel' ? 'glightbox-' + tuid : '', badges.length ? 'has-badge' : '']"
      :data-gallery="tuid"
      :data-type="getMediaType(item)"
      :data-width="item.type === 'video' ? '90vw' : ''"
      :data-height="item.type === 'video' ? '80vh' : ''"
      :data-embed-url="item.embed_url || ''"
      :target="item.type === 'reel' ? '_blank' : ''"
      rel="noopener noreferrer"
    >
      <component
        :is="getMediaTag(item)"
        :src="getImageUrl(item)"
        v-bind="getMediaAttributes(item)"
        loading="lazy"
        @loadstart="onMediaLoad(item, $event)"
        @error="onMediaError(item, $event, idx)"
        @loadedmetadata="onVideoMetadataLoaded(item, $event)"
      />
      <!--
        Значок разбора картинки (контент-мониторинг). Без badges его нет вовсе,
        поэтому обычная лента продукта коллаж видит прежним.
      -->
      <span
        v-if="badges.length"
        class="collage-badge"
        :class="badges[idx] ? 'is-done' : 'is-new'"
        @click.stop.prevent="$emit('badge-click', idx)"
      >
        <i :class="badges[idx] ? 'bi bi-check-lg' : 'bi bi-question-lg'"></i>
      </span>

      <div class="video-play" v-if="item.type === 'video' || item.type === 'reel'">
        <span></span>
      </div>
      <span class="content-type play" v-if="item.type === 'video'"></span>
    </a>
  </div>
</template>

<script>
import GLightbox from 'glightbox';

export default {
  name: 'CollageItems',
  props: {
    items: { type: Array, default: () => [] },
    className: { type: String, default: '' },
    padding: { type: Number, default: 0 },
    tuid: { type: String, default: '' },
    befLoad: { default: undefined },
    // по значку на плитку: true — картинка уже разобрана. Пустой массив = значков нет
    badges: { type: Array, default: () => [] },
  },
  data() {
    return {
      resizeTimeout: null,
      loadedMediaCount: 0,
      totalMediaCount: 0,
      lightboxInitialized: false,
    };
  },
  mounted() {
    if (!this.$options.propsData?.items.length || !this.$refs.div) return;

    this.$refs.div.className = this.$options.propsData.className;
    this.initializeCollage();
    window.addEventListener('resize', this.resizes);
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.resizes);
      this.resizeObserver.observe(this.$refs.div);
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizes);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  },
  methods: {
    /* ---------- helpers for URLs & attrs ---------- */
    getMediaUrl(item) {
      if (item.type === 'reel') return item.post_url;
      if (item.type === 'video' && item.video_url) return item.video_url;
      return item.image_url;
    },
    getMediaType(item) {
      return item.type === 'video' ? 'video' : item.type === 'photo' ? 'image' : item.type;
    },
    /**
     * Чем рисуем плитку. Видео с обложкой — картинкой: обложка это обычная
     * ссылка, которая живёт, а ссылка на сам файл у Instagram протухает, и тогда
     * плитка оставалась пустой. Без обложки поведение прежнее — тег video,
     * который показывает первый кадр. По клику в обоих случаях открывается видео.
     */
    getMediaTag(item) {
      if (item.type !== 'video') return 'img';
      return item.image_url ? 'img' : 'video';
    },
    getImageUrl(item) {
      if (item.type === 'video') return item.image_url || item.video_url;
      return item.image_url;
    },
    getMediaAttributes(item) {
      // только для видео без обложки — с обложкой плитка это уже <img>
      if (this.getMediaTag(item) === 'video') {
        return {
          muted: true,
          playsinline: true,
          preload: 'metadata',
          style: 'object-fit: cover;',
        };
      }
      return {};
    },

    /* ---------- loaders ---------- */
    onMediaLoad() {},
    onMediaError(item, event, idx) {
      // битый CDN: карусель клик→embed; одиночное/все битые — родитель меняет на Instagram embed
      if (item && item.type === "video" && item.embed_url && event && event.target) {
        const link = event.target.closest("a");
        if (link) {
          link.dataset.forceEmbed = "1";
          link.dataset.embedUrl = item.embed_url;
        }
        this.$emit("video-error", item, idx);
      } else if (item && item.type === "photo") {
        this.$emit("photo-error", item, idx);
      }
      this.onMediaLoadComplete();
    },
    onVideoMetadataLoaded() {
      this.onMediaLoadComplete();
    },

    /* ---------- collage ---------- */
    initializeCollage() {
      this.totalMediaCount = this.$options.propsData?.items.length || 0;
      this.loadedMediaCount = 0;
      this.lightboxInitialized = false;

      const items = this.items || this.$options.propsData?.items || [];
      Array.from(this.$refs.div.children).forEach((gl, idx) => {
        const el = gl.children[0];
        if (el.tagName === "IMG") {
          el.onload = this.onMediaLoadComplete;
          el.onerror = () => {
            const item = items[idx];
            if (item) this.$emit("photo-error", item, idx);
            this.onMediaLoadComplete();
          };
        }
      });

      if (this.totalMediaCount === 0) this.finalizeCollage();
    },
    onMediaLoadComplete() {
      this.loadedMediaCount++;
      if (this.loadedMediaCount >= this.totalMediaCount && !this.lightboxInitialized) {
        this.$nextTick(() => this.finalizeCollage());
      }
    },
    finalizeCollage() {
      if (!this.$refs.div || this.lightboxInitialized) return;

      this.createCollage();
      this.initializeLightbox();
      this.lightboxInitialized = true;
    },

    /* ---------- GLightbox ---------- */
    initializeLightbox() {
        const myGallery = GLightbox({
          selector: `.glightbox-${this.$options.propsData.tuid}`,
        videosWidth: '90vw',
        videosHeight: '80vh',
        loop: true,
        autoplayVideos: false,
        touchNavigation: true,
        keyboardNavigation: true,
        closeOnOutsideClick: true,
        moreText: 'See more',
        moreLength: 60,
        cssEfects: { fade: { in: 'fadeIn', out: 'fadeOut' } },
      });

      myGallery.on('slide_before_load', (data) => {
        if (data.slideConfig?.type === 'video' && !data.slideNode.dataset.videoInjected) {
          const originalVideoUrl = data.slideConfig.href;
          const tuid = this.$options.propsData.tuid;
          const anchors = this.$refs.div
            ? this.$refs.div.querySelectorAll(`a.glightbox-${tuid}`)
            : [];
          const anchor = anchors[data.slideIndex];
          const embedUrl =
            (anchor && (anchor.dataset.embedUrl || anchor.getAttribute('data-embed-url'))) ||
            '';
          const forceEmbed = !!(anchor && anchor.dataset.forceEmbed === '1');

          data.slideNode.dataset.processingVideo = 'true';
          data.slideNode.dataset.originalVideoUrl = originalVideoUrl;
          data.slideNode.dataset.originalType = 'video';

          data.slideConfig.type = 'inline';
          data.slideConfig.content = '<div class="custom-video-placeholder">Loading video...</div>';

          this.$nextTick(() => {
            setTimeout(() => {
              if (forceEmbed && embedUrl) {
                this.replaceWithEmbed(data, embedUrl);
              } else {
                this.replaceWithCustomVideo(data, originalVideoUrl, embedUrl);
              }
            }, 50);
          });
        }
      });

      myGallery.on('open', () => {
        setTimeout(() => {
          const brokenVideos = document.querySelectorAll('.gslide video[src]:not([controls])');
          brokenVideos.forEach(video => {
            video.controls = true;
            video.muted = true;
            video.playsInline = true;
            video.style.cssText = `
              max-width: 90vw; max-height: 80vh;
              width: auto; height: auto; object-fit: contain;`;
          });
        }, 300);
      });

      if (this.befLoad) {
        myGallery.on('slide_before_load', this.$options.propsData.befLoad);
      }
    },

    /* ---------- custom video injection ---------- */
    replaceWithEmbed(data, embedUrl) {
      const { slideNode } = data;
      const findMediaContainer = () => {
        let mediaContainer =
          slideNode.querySelector(".ginlined-content") ||
          slideNode.querySelector(".gslide-media") ||
          slideNode.querySelector(".custom-video-placeholder")?.parentElement;

        if (!mediaContainer) {
          let innerContent = slideNode.querySelector(".gslide-inner-content");
          if (!innerContent) {
            innerContent = document.createElement("div");
            innerContent.className = "gslide-inner-content";
            slideNode.appendChild(innerContent);
          }

          const gContainer = document.createElement("div");
          gContainer.className = "ginner-container";

          mediaContainer = document.createElement("div");
          mediaContainer.className = "gslide-media";

          gContainer.appendChild(mediaContainer);
          innerContent.appendChild(gContainer);
        }

        return mediaContainer;
      };

      try {
        const mediaContainer = findMediaContainer();
        mediaContainer.innerHTML = "";

        const wrapper = document.createElement("div");
        wrapper.style.cssText = `
          width: 100%; height: 100%; min-height: 70vh;
          display: flex !important; align-items: center; justify-content: center;
          background: #000; position: relative; z-index: 9999;`;

        const iframe = document.createElement("iframe");
        iframe.src = embedUrl;
        iframe.allow =
          "autoplay; encrypted-media; fullscreen; picture-in-picture;";
        iframe.allowFullscreen = true;
        iframe.style.cssText = `
          width: min(90vw, 420px) !important; height: min(80vh, 740px) !important;
          max-width: 90vw !important; max-height: 80vh !important;
          border: 0; background: #000; z-index: 10000; position: relative;`;

        wrapper.appendChild(iframe);
        mediaContainer.appendChild(wrapper);

        slideNode.classList.add("loaded");
        slideNode.classList.remove("loading");
        slideNode.dataset.videoInjected = "true";

        slideNode
          .querySelectorAll(".gloader, .gslide-description, .custom-video-placeholder")
          .forEach((el) => el.remove());
      } catch (error) {
        // embed injection failed silently
      }
    },

    replaceWithCustomVideo(data, videoUrl, embedUrl) {
      const { slideNode } = data;
      
      const findMediaContainer = () => {
        let mediaContainer = slideNode.querySelector('.ginlined-content') || 
                            slideNode.querySelector('.gslide-media') ||
                            slideNode.querySelector('.custom-video-placeholder')?.parentElement;
        
        if (!mediaContainer) {
          let innerContent = slideNode.querySelector('.gslide-inner-content');
          if (!innerContent) {
            innerContent = document.createElement('div');
            innerContent.className = 'gslide-inner-content';
            slideNode.appendChild(innerContent);
          }
          
          const gContainer = document.createElement('div');
          gContainer.className = 'ginner-container';
          
          mediaContainer = document.createElement('div');
          mediaContainer.className = 'gslide-media';
          
          gContainer.appendChild(mediaContainer);
          innerContent.appendChild(gContainer);
        }
        
        return mediaContainer;
      };
      
      const injectVideo = () => {
        try {
          const mediaContainer = findMediaContainer();
          mediaContainer.innerHTML = '';
          
                  const wrapper = document.createElement('div');
        wrapper.style.cssText = `
          width: 100%; height: 100%;
          display: flex !important; align-items: center; justify-content: center;
          background: #000; position: relative; z-index: 9999;`;
        
        const video = document.createElement('video');
        video.src = videoUrl;
        video.controls = true;
        video.muted = true;
        video.playsInline = true;
        video.preload = 'metadata';
        video.autoplay = false;
        video.style.cssText = `
          max-width: 90vw !important; max-height: 80vh !important;
          width: auto !important; height: auto !important; 
          object-fit: contain !important; display: block !important;
          background: #000; z-index: 10000; position: relative;`;

          video.onerror = () => {
            if (embedUrl) this.replaceWithEmbed(data, embedUrl);
          };
          
          wrapper.appendChild(video);
          mediaContainer.appendChild(wrapper);
          
          slideNode.classList.add('loaded');
          slideNode.classList.remove('loading');
          
          slideNode.querySelectorAll('.gloader').forEach(loader => {
            loader.style.display = 'none';
          });
          
          slideNode.querySelectorAll('.gslide-description').forEach(desc => {
            desc.remove();
          });
          
          slideNode.style.cssText += 'display: block !important; visibility: visible !important; opacity: 1 !important;';
          mediaContainer.style.cssText += 'display: block !important; visibility: visible !important; opacity: 1 !important;';
          wrapper.style.cssText += 'display: flex !important; visibility: visible !important; opacity: 1 !important;';
          
          slideNode.querySelectorAll('.gloader, .gslide-description, .custom-video-placeholder').forEach(el => {
            el.remove();
          });
          
          video.load();
          
          setTimeout(() => {
            if (video.style.display === 'none' || wrapper.style.display === 'none') {
              video.style.cssText += 'display: block !important;';
              wrapper.style.cssText += 'display: flex !important;';
            }
          }, 100);
          
        } catch (error) {
          if (embedUrl) this.replaceWithEmbed(data, embedUrl);
        }
      };
      
      // Делаем одну попытку инъекции, но проверяем готовность контейнера
      const attemptInjection = (attempt = 1, maxAttempts = 3) => {
        if (attempt > maxAttempts) return;
        
        const container = slideNode.querySelector('.gslide-media');
        if (container && !slideNode.dataset.videoInjected) {
          slideNode.dataset.videoInjected = 'true';
          injectVideo();
        } else if (!slideNode.dataset.videoInjected) {
          setTimeout(() => attemptInjection(attempt + 1, maxAttempts), 50 * attempt);
        }
      };
      
      attemptInjection();
    },

    /* ---------- collage calculation (unchanged) ---------- */
    createCollage() {
      if (!this.$refs.div) return;
      
      const settings = {
        albumWidth: 0,
        allowPartialLastRow: false,
        direction: 'vertical',
        display: 'inline-block',
        effect: 'default',
        fadeSpeed: 2000,
        padding: 0,
        targetHeight: 200,
      };
      
      const resizeRow = (obj, row, settings) => {
        const extras = settings.padding * (obj.length - 1) + obj.length * obj[0][3];
        const available = settings.albumWidth - extras;
        const ratio = available / (row - extras);
        let track = extras;

        obj.forEach((o, i) => {
          let w = Math.floor(o[1] * ratio);
          let h = Math.floor(o[2] * ratio);
          const last = i === obj.length - 1;

          track += w;
          if (last && track < settings.albumWidth) w += settings.albumWidth - track;

          w--;
          Object.assign(o[0].style, {
            width: w + 'px',
            height: h + 'px',
            marginBottom: settings.padding + 'px',
            marginRight: last ? 0 : settings.padding + 'px',
            display: settings.display,
            verticalAlign: 'bottom',
            overflow: 'hidden',
          });
        });
      };

      let row = 0,
        elements = [],
        rowNum = 1;

      settings.padding = this.$options.propsData.padding || 0;
      settings.albumWidth = this.$refs.div.clientWidth;
      if (!settings.albumWidth) return;

      [...this.$refs.div.children].forEach((gl, idx) => {
        const el = gl.children[0];

        // reset inline styles
        ['width', 'height', 'marginBottom', 'marginRight', 'display', 'verticalAlign', 'overflow'].forEach(
          (p) => (el.style[p] = '')
        );

        let w, h;
        if (el.tagName === 'VIDEO') {
          w = el.videoWidth || 300;
          h = el.videoHeight || 169;
        } else {
          w = el.naturalWidth || 300;
          h = el.naturalHeight || 200;
        }

        const bw = 0,
          bh = 0;
        const nw = Math.ceil((w / h) * settings.targetHeight);
        const nh = Math.ceil(settings.targetHeight);

        elements.push([el, nw, nh, bw, bh]);
        row += nw + bw + settings.padding;

        if (row > settings.albumWidth && elements.length) {
          resizeRow(elements, row - settings.padding, settings, rowNum++);
          row = 0;
          elements = [];
        }
        if (idx === this.$refs.div.children.length - 1 && elements.length) {
          resizeRow(elements, row, settings, rowNum++);
        }
      });
    },

    /* ---------- resize debounce ---------- */
    resizes() {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => this.createCollage(), 300);
    },
  },
};
</script>

